import { useMemo } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import InventoryIcon from "@mui/icons-material/Inventory";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import moment from "moment";

import "react-circular-progressbar/dist/styles.css";

import { CustomConnector } from "./CustomConnecter";
import { CustomStepIcon } from "./CustomIcon";

const TIMELINE_ICONS = {
  added_to_cart: ShoppingCartIcon,
  checkout: ShoppingBagIcon,
  product_scraped: InventoryIcon,
};

export const ProductTimeline = ({ line }) => {
  const { steps, currentStatus } = useMemo(() => {
    if (!line) {
      return {
        steps: [],
        currentStatus: 0,
      };
    }

    const apiTimeline = Array.isArray(line.timeline) ? line.timeline : [];

    const generatedSteps = apiTimeline.map((item, index) => ({
      key: item.event,

      label: item.event
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),

      timestamp: item.timestamp || null,

      // Every event received from API is completed
      completed: true,

      // React component used by CustomStepIcon
      icon: TIMELINE_ICONS[item.event] || CheckCircleIcon,

      // Optional animation properties
      animate_type: item.animate_type,
      duration: item.duration,

      index,
    }));

    return {
      steps: generatedSteps,

      // Last event is the current event
      currentStatus: generatedSteps.length > 0 ? generatedSteps.length - 1 : 0,
    };
  }, [line]);

  if (!line || !steps.length) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        mt: 1,
        // backgroundColor: "red",
      }}
    >
      <Accordion
        sx={{
          backgroundColor: "transparent",
          boxShadow: "none",
          margin: 0,

          "&.Mui-expanded": {
            margin: 0,
          },

          "&:before": {
            display: "none",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="timeline-panel-content"
          id="timeline-panel-header"
          sx={{
            minHeight: 30,
            height: 30,
            px: 1,

            "&.Mui-expanded": {
              minHeight: 30,
              height: 30,
            },

            "& .MuiAccordionSummary-content": {
              margin: 0,
            },

            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              minWidth: {
                xs: "auto",
                sm: 50,
              },
              mr: 1,
            }}
          >
            Timeline
          </Typography>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            px: 0,
            pt: 1,
            pb: 0,
            m: 0,
          }}
        >
          <Stepper
            alternativeLabel
            activeStep={currentStatus}
            connector={<CustomConnector />}
          >
            {steps.map((step, index) => (
              <Step key={`${step.key}-${index}`} completed={step.completed}>
                <StepLabel
                  StepIconComponent={(props) => (
                    <CustomStepIcon
                      {...props}
                      statusHistories={steps}
                      currentStatus={currentStatus}
                    />
                  )}
                  sx={{
                    "& .MuiStepLabel-label": {
                      marginTop: "8px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "rgba(0,0,0,0.8)",
                      }}
                    >
                      {step.label}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.4,
                        fontSize: 11,
                        whiteSpace: "nowrap",
                        color: "rgba(0,0,0,0.55)",
                      }}
                    >
                      {step.timestamp
                        ? moment(step.timestamp).format("DD MMM YYYY hh:mm A")
                        : ""}
                    </Typography>
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
